import { Formik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import FullPageLoading from 'components/FullPageLoading'
import { _, withCapture } from 'utils'
import { UpdateCinema, CinemaFragment } from 'generated/graphql'
import { useEventSlug } from 'hooks'
import FormTabs from '../forms/FormTabs'
import { getChangedValues } from '../forms/utils'
import { updateCinema } from './graphql/mutation'
import Movies from './tabs/Movies'
import Settings from './tabs/Settings'
import { showNotification } from '../notifications'
import { showErrorNotification } from '../notifications/notificationsSlice'
import { getEventData } from '../events/eventSlice'
import MovieTags from './tabs/MovieTags'

const transformDataForForm = (data: UpdateCinema) => ({
    parent: {
        name: data?.parent?.name || '',
        image: data?.parent?.image || '',
        helpContent: data?.parent?.helpContent || '',
        helpContentType: data?.parent?.helpContentType || '',
        published: data?.parent?.published || false
    }
})

const Cinema = () => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const [cinema, setCinema] = useState<CinemaFragment>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [initialValues, setInitialValues] = useState<UpdateCinema>()

    useEffect(() => {
        setInitialValues(transformDataForForm(cinema))
    }, [cinema])

    const handleSubmit = withCapture(async (values, { resetForm }) => {
        setIsUploading(true)
        const submitValues = getChangedValues(initialValues, values)
        const response = await updateCinema(cinema.id, submitValues)
        setIsUploading(false)

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }

        const newValues = {
            parent: {
                ...values.parent,
                image: response.data.updateCinema.parent.image
            }
        }
        resetForm({ values: newValues })
        setInitialValues(newValues)
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.update_success'),
        }))

        // If the name/published fields are changed, re-fetch event data for Sidebar
        if (submitValues?.parent?.name || submitValues?.parent?.published !== undefined) {
            dispatch(getEventData(eventSlug))
        }
    })

    const MovieMemo = useMemo(() => (
        () => <Movies onGetCinema={(_cinema) => setCinema(_cinema)} />
    ), [])

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            <>
                <FormTabs
                    tabs={[
                        {
                            id: 'movies',
                            name: _('cinema.tabs.movies'),
                            Component: MovieMemo
                        },
                        {
                            id: 'setting',
                            name: _('cinema.tabs.setting'),
                            Component: Settings
                        },
                        {
                            id: 'tags',
                            name: _('cinema.tabs.tags'),
                            Component: MovieTags
                        },
                    ]}
                    showHelpContent
                    showSpaceOpened
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    )
}

export default Cinema
